// Employee Self-Service — for team_member role.
// Read-only profile + own attendance / leave / payslips / documents.
import React, { useEffect, useState } from 'react';
import {
  User, Calendar, Wallet, Clock, FileText, BookOpen, CreditCard, LogOut,
  ChevronRight, RefreshCw, AlertCircle, Briefcase, Building2, Package, Megaphone,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';

export default function EmployeeSelfService() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [payslips, setPayslips] = useState([]);
  const [leave, setLeave] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [docs, setDocs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/hr/me/profile');
        if (data?.linked === false) {
          setError(`No employee record is linked to your account (${data.user?.email}). Admins and managers do not have a personal profile here — go to your main workspace instead.`);
          return;
        }
        setProfile(data);
        const [ps, lv, at, dc, ast, ann] = await Promise.all([
          api.get('/hr/me/payslips'),
          api.get('/hr/me/leave'),
          api.get('/hr/me/attendance'),
          api.get('/hr/me/documents'),
          api.get('/hr/me/assets'),
          api.get('/hr/announcements'),
        ]);
        setPayslips(ps.data.items || []);
        setLeave(lv.data.items || []);
        setAttendance(at.data.items || []);
        setDocs(dc.data.items || []);
        setAssets(ast.data.items || []);
        setAnnouncements(ann.data.items || []);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Could not load your profile');
      }
    })();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6" data-testid="ess-error">
        <div className="bg-white rounded border p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <h2 className="font-display font-bold text-xl mb-2">Workspace not ready</h2>
          <p className="text-sm text-neutral-500 mb-5">{error}</p>
          <button onClick={() => { logout(); navigate('/login'); }} className="btn-outline">Sign out</button>
        </div>
      </div>
    );
  }
  if (!profile) return <div className="min-h-screen bg-neutral-50 flex items-center justify-center text-neutral-400">Loading your workspace…</div>;

  const initials = `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`;
  const TABS = [
    { key: 'overview', label: 'Overview', icon: User },
    { key: 'announcements', label: 'Announcements', icon: Megaphone, badge: announcements.length || null },
    { key: 'attendance', label: 'My attendance', icon: Clock },
    { key: 'leave', label: 'My leave', icon: Calendar },
    { key: 'payslips', label: 'My payslips', icon: Wallet },
    { key: 'assets', label: 'My assets', icon: Package, badge: assets.length || null },
    { key: 'documents', label: 'My documents', icon: FileText },
    { key: 'learning', label: 'Learning', icon: BookOpen },
  ];

  return (
    <div className="min-h-screen bg-neutral-50 flex" data-testid="ess-shell">
      <aside className="w-64 bg-white border-r flex-shrink-0">
        <div className="px-5 py-5 border-b">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 rounded-full bg-navy text-amber font-bold flex items-center justify-center">{initials}</div>
            <div className="min-w-0">
              <div className="font-display font-semibold truncate" data-testid="ess-name">{profile.first_name} {profile.last_name}</div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">{profile.employee_no || profile.employee_code}</div>
            </div>
          </div>
          <div className="text-xs text-neutral-500 space-y-1">
            <div className="flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> {profile.salary_grade || 'Unassigned'}</div>
            <div className="flex items-center gap-1.5"><Building2 className="w-3 h-3" /> {user?.org_name}</div>
          </div>
        </div>
        <nav className="px-2 py-3">
          {TABS.map((t) => {
            const I = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded text-sm transition ${active ? 'bg-navy text-white font-medium' : 'text-neutral-700 hover:bg-neutral-50'}`}
                data-testid={`ess-tab-${t.key}`}>
                <I className="w-4 h-4" />
                <span className="flex-1 text-left">{t.label}</span>
                {t.badge ? <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${active ? 'bg-amber text-navy' : 'bg-amber/15 text-amber-700'}`}>{t.badge}</span> : null}
              </button>
            );
          })}
        </nav>
        <div className="px-2 py-3 border-t mt-auto">
          <button onClick={() => { logout(); navigate('/login'); }} className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-rose-600 hover:bg-rose-50"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">{user?.org_name}</div>
            <h1 className="font-display font-bold text-xl text-navy">My Workspace</h1>
          </div>
          <button onClick={() => window.location.reload()} className="text-xs uppercase font-semibold px-2 py-1 rounded border hover:bg-neutral-100"><RefreshCw className="w-3 h-3 inline" /> Refresh</button>
        </header>

        <div className="p-6">
          {tab === 'overview' && <Overview profile={profile} payslips={payslips} leave={leave} attendance={attendance} announcements={announcements} />}
          {tab === 'announcements' && <AnnouncementsTab items={announcements} />}
          {tab === 'attendance' && <AttendanceTab items={attendance} />}
          {tab === 'leave' && <LeaveTab items={leave} />}
          {tab === 'payslips' && <PayslipsTab items={payslips} />}
          {tab === 'assets' && <AssetsTab items={assets} />}
          {tab === 'documents' && <DocsTab items={docs} />}
          {tab === 'learning' && <LearningStub />}
        </div>
      </main>
    </div>
  );
}

function KPI({ label, value, sub }) {
  return (
    <div className="bg-white border rounded p-4">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{label}</div>
      <div className="font-display font-bold text-2xl text-navy mt-1">{value}</div>
      {sub && <div className="text-[11px] text-neutral-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Overview({ profile, payslips, leave, attendance, announcements }) {
  const presentDays = attendance.filter((a) => a.clock_in).length;
  const monthlyNet = payslips[0]?.net_pay;
  const pendingLeave = leave.filter((l) => l.status === 'pending').length;
  const pinned = (announcements || []).find((a) => a.pinned) || (announcements || [])[0];
  return (
    <div data-testid="ess-overview">
      {pinned && (
        <div className="mb-5 bg-amber/10 border border-amber/30 rounded p-4 flex items-start gap-3" data-testid="ess-pinned-announcement">
          <div className="h-8 w-8 rounded bg-amber flex items-center justify-center flex-shrink-0"><Megaphone className="w-4 h-4 text-navy" /></div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-semibold text-navy">{pinned.title}</div>
              {pinned.priority === 'urgent' && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-rose-600 text-white">urgent</span>}
              {pinned.priority === 'high' && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-600 text-white">high</span>}
            </div>
            <p className="text-sm text-neutral-700 mt-1 leading-relaxed">{pinned.body}</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPI label="Days present (recent)" value={presentDays} sub={`out of ${attendance.length} logged`} />
        <KPI label="Last net pay" value={monthlyNet ? `₦${monthlyNet.toLocaleString()}` : '—'} sub={payslips[0]?.period || 'no payslip yet'} />
        <KPI label="Pending leave" value={pendingLeave} sub={pendingLeave ? 'awaiting approval' : 'all clear'} />
        <KPI label="Base salary" value={profile.base_salary ? `₦${profile.base_salary.toLocaleString()}` : '—'} sub={profile.salary_grade || ''} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border rounded p-5">
          <h3 className="font-display font-semibold text-lg text-navy mb-3">Profile</h3>
          <dl className="text-sm space-y-2">
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Employee #</dt><dd className="font-mono">{profile.employee_no}</dd></div>
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Email</dt><dd>{profile.email}</dd></div>
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Phone</dt><dd>{profile.phone || '—'}</dd></div>
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Hired</dt><dd>{profile.hire_date || '—'}</dd></div>
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Status</dt><dd>{profile.employment_status}</dd></div>
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Type</dt><dd>{profile.employment_type}</dd></div>
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Bank</dt><dd>{profile.bank_name || '—'}</dd></div>
          </dl>
        </div>
        <div className="bg-white border rounded p-5">
          <h3 className="font-display font-semibold text-lg text-navy mb-3">Authentication & cards</h3>
          <div className="text-sm space-y-2.5">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Active auth methods</div>
              <div className="flex flex-wrap gap-1.5">
                {(profile.auth_methods || ['password']).map((m) => (
                  <span key={m} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">{m}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Cards on file</div>
              <div className="font-mono text-sm">{(profile.card_ids || []).length} card(s) issued</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AttendanceTab({ items }) {
  return (
    <Table cols={['Date', 'Clock in', 'Clock out', 'Source']} rows={items.map((a) => [a.date, a.clock_in ? new Date(a.clock_in).toLocaleTimeString() : '—', a.clock_out ? new Date(a.clock_out).toLocaleTimeString() : '—', a.source || 'manual'])} testid="ess-attendance" />
  );
}

function LeaveTab({ items }) {
  return (
    <Table cols={['Type', 'Days', 'Start', 'End', 'Status']} rows={items.map((l) => [l.leave_type, l.days, l.start_date, l.end_date, l.status])} testid="ess-leave" />
  );
}

function PayslipsTab({ items }) {
  return (
    <Table cols={['Period', 'Gross', 'Tax', 'Pension', 'Net pay']} rows={items.map((p) => [p.period, fmt(p.gross_pay), fmt(p.tax), fmt(p.pension), fmt(p.net_pay)])} testid="ess-payslips" />
  );
}

function DocsTab({ items }) {
  return (
    <Table cols={['Title', 'Kind', 'Issue', 'Expiry', 'File']} rows={items.map((d) => [d.title, d.kind, d.issue_date || '—', d.expiry_date || '—', d.file_url ? <a href={d.file_url} target="_blank" rel="noreferrer" className="text-navy underline">Open</a> : '—'])} testid="ess-docs" />
  );
}

function LearningStub() {
  return <div className="bg-white border border-dashed rounded p-10 text-center text-neutral-400">Your enrolled courses will appear here.</div>;
}

function AssetsTab({ items }) {
  if (items.length === 0) return <div className="bg-white border border-dashed rounded p-10 text-center text-neutral-400">No assets currently assigned to you.</div>;
  return (
    <div data-testid="ess-assets" className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {items.map((a) => (
        <div key={a.id} className="bg-white border rounded p-4">
          <div className="flex justify-between items-start mb-2">
            <div>
              <div className="font-display font-semibold text-base text-navy">{a.name}</div>
              <div className="text-[11px] uppercase tracking-wider font-mono text-neutral-500">{a.tag}</div>
            </div>
            <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{a.status}</span>
          </div>
          <dl className="text-xs space-y-1 mt-2">
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Category</dt><dd>{a.category || '—'}</dd></div>
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Serial</dt><dd className="font-mono">{a.serial_number || '—'}</dd></div>
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Location</dt><dd>{a.location || '—'}</dd></div>
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Condition</dt><dd>{a.condition || '—'}</dd></div>
            <div className="grid grid-cols-2"><dt className="text-neutral-500">Assigned</dt><dd>{(a.assigned_at || '').slice(0, 10) || '—'}</dd></div>
          </dl>
        </div>
      ))}
    </div>
  );
}

function AnnouncementsTab({ items }) {
  if (items.length === 0) return <div className="bg-white border border-dashed rounded p-10 text-center text-neutral-400">No announcements at this time.</div>;
  const priorityBadge = (p) => ({ urgent: 'bg-rose-600 text-white', high: 'bg-amber-600 text-white', normal: 'bg-navy/10 text-navy', low: 'bg-neutral-100 text-neutral-500' })[p] || 'bg-neutral-100';
  return (
    <div data-testid="ess-announcements" className="space-y-3">
      {items.map((a) => (
        <div key={a.id} className={`bg-white border rounded p-4 ${a.pinned ? 'border-amber/40 bg-amber/5' : ''}`}>
          <div className="flex justify-between items-start mb-2 gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {a.pinned && <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber text-navy">📌 Pinned</span>}
                <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${priorityBadge(a.priority)}`}>{a.priority || 'normal'}</span>
                <span className="text-[10px] uppercase tracking-wider text-neutral-400">{a.kind}</span>
              </div>
              <h3 className="font-display font-semibold text-base text-navy">{a.title}</h3>
            </div>
            <div className="text-[11px] text-neutral-500 whitespace-nowrap">{(a.published_at || '').slice(0, 10)}</div>
          </div>
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">{a.body}</p>
        </div>
      ))}
    </div>
  );
}

function Table({ cols, rows, testid }) {
  return (
    <div className="bg-white border rounded overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="border-b bg-neutral-50/60">
          <tr>{cols.map((c) => <th key={c} className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b hover:bg-neutral-50/40">{r.map((cell, j) => <td key={j} className="px-3 py-2.5">{cell ?? '—'}</td>)}</tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={cols.length} className="px-6 py-10 text-center text-neutral-400">No records yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function fmt(n) { return n != null ? `₦${Number(n).toLocaleString()}` : '—'; }
